// ─────────────────────────────────────────────────────────────────────────────
// contamination-card.component.ts
// Visualiza qué % del espacio Postobón está ocupado por marcas ajenas.
// El objetivo de negocio es que este número esté cercano a 0%.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

// ── Tipos públicos ─────────────────────────────────────────────────────────

export interface ContaminationImageRow {
  imageUrl:     string;
  timestamp:    string;
  ownCount:     number;
  foreignCount: number;
  pctForeign:   number;
}

export interface ContaminationCardConfig {
  title: string;

  // KPI principal
  overallPct:   number;   // % objetos ajenos en espacio postobon
  ownCount:     number;
  foreignCount: number;
  totalInSpace: number;
  vaciosTotal:  number;   // espacios vacíos detectados (tag='vacios')

  // Ranking de marcas que contaminan
  contaminantBrands:     { brand: string; count: number; pct: number; company: string }[];

  // Por categoría del producto contaminante
  contaminantCategories: { category: string; count: number; pct: number }[];

  // Tabla por imagen
  imageRows: ContaminationImageRow[];
}

@Component({
  selector:        'app-contamination-card',
  standalone:      true,
  imports:         [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="cc-wrap">

  <div class="cc-header">
    <span class="cc-title">{{ config?.title ?? 'Contaminación en Nevera Postobón' }}</span>
    @if (loading) { <span class="cc-badge cc-badge--load">Cargando…</span> }
  </div>

  @if (loading) {
    <div class="cc-skeleton">
      <div class="cc-sk cc-sk--gauge"></div>
      <div class="cc-sk cc-sk--side"></div>
    </div>
  } @else if (config) {

    <!-- Fila principal: gauge + desglose derecho -->
    <div class="cc-main-row">

      <!-- Gauge circular -->
      <div class="cc-gauge-wrap">
        <svg class="cc-gauge" viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg">
          <circle cx="80" cy="80" r="60" fill="none" stroke="#1e2235" stroke-width="18"/>
          <circle cx="80" cy="80" r="60" fill="none"
                  [attr.stroke]="gaugeColor"
                  stroke-width="18"
                  stroke-linecap="round"
                  stroke-dasharray="377"
                  [attr.stroke-dashoffset]="gaugeDashOffset"
                  transform="rotate(-90 80 80)"
                  style="transition: stroke-dashoffset .6s ease"/>
          <text x="80" y="74" text-anchor="middle" fill="#f1f5f9" font-size="26" font-weight="700">
            {{ config.overallPct.toFixed(0) }}%
          </text>
          <text x="80" y="96" text-anchor="middle" fill="#94a3b8" font-size="11">
            Contaminación
          </text>
        </svg>
        <div class="cc-gauge-status" [style.color]="gaugeColor">{{ statusLabel }}</div>
        <div class="cc-gauge-legend">
          <span><span class="cc-dot" style="background:#6366f1"></span>Postobón propios: {{ config.ownCount }}</span>
          <span><span class="cc-dot" style="background:#ef4444"></span>Marcas ajenas: {{ config.foreignCount }}</span>
          @if (config.vaciosTotal > 0) {
            <span><span class="cc-dot" style="background:#f59e0b"></span>Vacíos detectados: {{ config.vaciosTotal }}</span>
          }
        </div>
      </div>

      <!-- Panel derecho: brands + categorías -->
      <div class="cc-detail">

        <!-- Marcas contaminantes -->
        <div class="cc-section-title">Marcas que contaminan el espacio</div>
        <div class="cc-brand-list">
          @for (b of config.contaminantBrands.slice(0, 8); track b.brand) {
            <div class="cc-brand-row">
              <div class="cc-brand-info">
                <span class="cc-brand-dot" [style.background]="brandColor(b.company)"></span>
                <span class="cc-brand-name">{{ b.brand }}</span>
                <span class="cc-brand-company">{{ b.company }}</span>
              </div>
              <div class="cc-brand-bar-wrap">
                <div class="cc-brand-bar"
                     [style.width.%]="b.pct"
                     [style.background]="brandColor(b.company)">
                </div>
              </div>
              <div class="cc-brand-nums">
                <strong>{{ b.count }}</strong>
                <span>{{ b.pct.toFixed(0) }}%</span>
              </div>
            </div>
          }
        </div>

        <!-- Categorías contaminantes -->
        <div class="cc-section-title" style="margin-top:14px;">Por categoría</div>
        <div class="cc-cat-chips">
          @for (cat of config.contaminantCategories; track cat.category) {
            <div class="cc-cat-chip">
              <span class="cc-cat-chip__label">{{ cat.category }}</span>
              <strong class="cc-cat-chip__count">{{ cat.count }}</strong>
              <span class="cc-cat-chip__pct">{{ cat.pct.toFixed(0) }}%</span>
            </div>
          }
        </div>

      </div>
    </div>

    <!-- Tabla por imagen/ejecución -->
    @if (config.imageRows.length) {
      <div class="cc-section-title" style="margin-top:16px;">Detalle por ejecución</div>
      <div class="cc-table-wrap">
        <table class="cc-table">
          <thead>
            <tr>
              <th>Imagen</th>
              <th>Propios</th>
              <th>Ajenos</th>
              <th>% Contaminación</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            @for (row of config.imageRows; track row.imageUrl) {
              <tr [class.cc-row--danger]="row.pctForeign > 30"
                  [class.cc-row--warn]="row.pctForeign > 10 && row.pctForeign <= 30">
                <td>
                  <div class="cc-thumb-cell">
                    <img [src]="row.imageUrl" alt="" loading="lazy" />
                    <span>{{ formatDate(row.timestamp) }}</span>
                  </div>
                </td>
                <td class="cc-num cc-num--own">{{ row.ownCount }}</td>
                <td class="cc-num cc-num--foreign">{{ row.foreignCount }}</td>
                <td>
                  <div class="cc-pct-bar">
                    <div [style.width.%]="row.pctForeign"
                         [style.background]="pctColor(row.pctForeign)">
                    </div>
                    <span>{{ row.pctForeign.toFixed(0) }}%</span>
                  </div>
                </td>
                <td>
                  <span class="cc-chip"
                        [class.cc-chip--ok]="row.pctForeign <= 10"
                        [class.cc-chip--warn]="row.pctForeign > 10 && row.pctForeign <= 30"
                        [class.cc-chip--danger]="row.pctForeign > 30">
                    {{ row.pctForeign <= 10 ? 'OK' : row.pctForeign <= 30 ? 'Atención' : 'Crítico' }}
                  </span>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

  } @else {
    <div class="cc-empty">Sin datos para el período seleccionado.</div>
  }

</div>
  `,
  styles: [`
    :host { display: block; }

    .cc-wrap {
      background: #1a1d2e;
      border-radius: 16px;
      padding: 20px;
      color: #e2e8f0;
      font-family: inherit;
    }

    /* Header */
    .cc-header {
      display: flex; align-items: center; gap: 10px; margin-bottom: 16px;
    }
    .cc-title { font-size: 15px; font-weight: 600; color: #f1f5f9; flex: 1; }
    .cc-badge { font-size: 11px; background: #0f1120; color: #64748b; border-radius: 20px; padding: 3px 10px; }
    .cc-badge--load { color: #6366f1; }

    /* Skeleton */
    .cc-skeleton { display: grid; grid-template-columns: 220px 1fr; gap: 16px; height: 280px; }
    .cc-sk { background: #0f1120; border-radius: 12px; animation: pulse 1.5s ease-in-out infinite alternate; }
    @keyframes pulse { from { opacity: .4; } to { opacity: .7; } }

    /* Main row */
    .cc-main-row {
      display: grid;
      grid-template-columns: 220px 1fr;
      gap: 20px;
      align-items: start;
    }

    /* Gauge */
    .cc-gauge-wrap {
      display: flex; flex-direction: column; align-items: center; gap: 10px;
    }
    .cc-gauge { width: 160px; height: 160px; }
    .cc-gauge-status { font-size: 13px; font-weight: 700; }
    .cc-gauge-legend {
      display: flex; flex-direction: column; gap: 4px;
      font-size: 11px; color: #94a3b8;
      span { display: flex; align-items: center; gap: 5px; }
    }
    .cc-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

    /* Detail panel */
    .cc-detail { display: flex; flex-direction: column; }
    .cc-section-title {
      font-size: 11px; font-weight: 600; color: #64748b;
      text-transform: uppercase; letter-spacing: .05em; margin-bottom: 8px;
    }

    /* Brand list */
    .cc-brand-list { display: flex; flex-direction: column; gap: 6px; }
    .cc-brand-row {
      display: grid; grid-template-columns: 1fr 120px 60px;
      align-items: center; gap: 10px;
    }
    .cc-brand-info {
      display: flex; align-items: center; gap: 6px; min-width: 0;
    }
    .cc-brand-dot   { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
    .cc-brand-name  { font-size: 12px; color: #e2e8f0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .cc-brand-company { font-size: 10px; color: #64748b; white-space: nowrap; margin-left: 2px; }
    .cc-brand-bar-wrap { background: #1e2235; border-radius: 4px; height: 7px; overflow: hidden; }
    .cc-brand-bar  { height: 100%; border-radius: 4px; transition: width .4s; }
    .cc-brand-nums {
      display: flex; flex-direction: column; align-items: flex-end;
      strong { font-size: 13px; color: #f1f5f9; }
      span   { font-size: 10px; color: #64748b; }
    }

    /* Category chips */
    .cc-cat-chips { display: flex; gap: 8px; flex-wrap: wrap; }
    .cc-cat-chip {
      background: #0f1120; border-radius: 10px; padding: 8px 12px;
      display: flex; flex-direction: column; align-items: center; gap: 2px;
      min-width: 90px;
    }
    .cc-cat-chip__label { font-size: 10px; color: #64748b; text-align: center; }
    .cc-cat-chip__count { font-size: 18px; font-weight: 700; color: #ef4444; }
    .cc-cat-chip__pct   { font-size: 10px; color: #94a3b8; }

    /* Table */
    .cc-table-wrap { overflow-x: auto; border-radius: 10px; border: 1px solid #1e2235; }
    .cc-table {
      width: 100%; border-collapse: collapse; font-size: 12px;
      th, td { padding: 8px 10px; border-bottom: 1px solid #1e2235; text-align: left; }
      th { color: #64748b; font-weight: 600; background: #0f1120; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
    }
    .cc-row--danger td { background: rgba(239,68,68,.06); }
    .cc-row--warn   td { background: rgba(245,158,11,.05); }

    .cc-thumb-cell {
      display: flex; align-items: center; gap: 8px;
      img { width: 48px; height: 32px; object-fit: cover; border-radius: 5px; display: block; }
      span { font-size: 11px; color: #94a3b8; white-space: nowrap; }
    }
    .cc-num { font-weight: 600; text-align: center; }
    .cc-num--own     { color: #6366f1; }
    .cc-num--foreign { color: #ef4444; }

    .cc-pct-bar {
      display: flex; align-items: center; gap: 8px;
      div { height: 6px; border-radius: 3px; min-width: 4px; flex-shrink: 0; transition: width .3s; }
      span { font-size: 11px; color: #e2e8f0; white-space: nowrap; }
    }

    .cc-chip {
      padding: 3px 8px; border-radius: 20px; font-size: 10px; font-weight: 600;
    }
    .cc-chip--ok     { background: rgba(34,197,94,.15);  color: #4ade80; }
    .cc-chip--warn   { background: rgba(245,158,11,.15); color: #fcd34d; }
    .cc-chip--danger { background: rgba(239,68,68,.15);  color: #f87171; }

    .cc-empty { text-align: center; color: #64748b; padding: 40px; }

    @media (max-width: 700px) {
      .cc-main-row { grid-template-columns: 1fr; }
      .cc-brand-row { grid-template-columns: 1fr 80px 50px; }
    }
  `],
})
export class ContaminationCardComponent implements OnChanges {

  @Input() config?: ContaminationCardConfig | null;
  @Input() loading = false;

  ngOnChanges(): void {}

  get gaugeColor(): string {
    if (!this.config) return '#64748b';
    const p = this.config.overallPct;
    return p <= 10 ? '#22c55e' : p <= 30 ? '#f59e0b' : '#ef4444';
  }

  get gaugeDashOffset(): number {
    if (!this.config) return 377;
    const pct = Math.min(100, this.config.overallPct);
    return 377 - (377 * pct) / 100;
  }

  get statusLabel(): string {
    if (!this.config) return '';
    const p = this.config.overallPct;
    return p <= 10 ? 'Excelente' : p <= 30 ? 'Atención' : 'Crítico';
  }

  brandColor(company: string): string {
    const lc = (company ?? '').toLowerCase();
    if (lc.includes('coca cola'))         return '#ef4444';
    if (lc.includes('pepsico'))           return '#3b82f6';
    if (lc.includes('bavaria'))           return '#f59e0b';
    if (lc.includes('central cervecera')) return '#8b5cf6';
    if (lc.includes('postobon'))          return '#6366f1';
    return '#64748b';
  }

  pctColor(pct: number): string {
    return pct <= 10 ? '#22c55e' : pct <= 30 ? '#f59e0b' : '#ef4444';
  }

  formatDate(raw: string): string {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw ?? '';
    return new Intl.DateTimeFormat('es-CO', {
      month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(d);
  }
}
